// Components/Tabs/EditBadgeMachineTypeTab.jsx

import React, { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";

// Importing components
import Slider from "../Slider/Slider";
import DataTable from "../DataTable/DataTable";
import { AddIcon, DeleteIcon } from "../AdminIcon";
import BadgeRequirementDialog from "../BadgeRequirementDialog";
import RemoveBadgeRequirementDialog from "../RemoveBadgeRequirementDialog";
import Backend from "../../../utils/api.js";
import { useDocumentTitle } from "../../../utils/useDocumentTitle";

/**
 * EditBadgeMachineTypeTab Component
 * 
 * This component allows administrators to edit the relationships between badges and machine types.
 * It displays a data table with existing relationships and provides functionalities to update or add new ones.
 * 
 * @param {Object} props - Component properties.
 * @param {Object} props.user - The current user object containing user details.
 * @param {string} props.labId - The unique identifier of the lab.
 * @param {Function} props.pushTab - Function to push a new tab onto the stack.
 * @param {Function} props.revert - Function to revert the tab stack by a specified number of steps.
 * @param {number} props.index - The current index in the tab stack for proper stacking order.
 */
function EditBadgeMachineTypeTab({ user, labId, pushTab, revert, index }) {
  useDocumentTitle("Admin: Edit Badge-Tools Tab – Pinpoint");
  const [adding, setAdding] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [revision, setRevision] = useState(0);
  const tableRef = useRef(null);
  const returnFocusRef = useRef(null);
  const loadBadgeRequirements = useCallback(() => Backend.getFullBadgeInfo(user.UID), [user.UID, revision]);

  return (
    <Slider
      name="Badge requirements for tools"
      navColor="rgb(50, 168, 82)"
      slide={true} 
      index={index}
      onClick={() => revert(2)} // Revert two steps when navigation bar is clicked
    >
      <div className="dataTableWrapper" ref={tableRef} tabIndex={-1}>
        {/* DataTable displays the list of badge-machine-type relationships */}
        <DataTable
          title="Badge requirements for tools"
          columns={[
            { title: "Badge ID", field: "badgeclass_entity_id" },
            { title: "Tool name", field: "name" },
            { title: "Badge name", field: "badge_name", editable: "never" },
            { title: "Description", field: "description", editable: "never" },
          ]}
          dataFunction={loadBadgeRequirements}
          customActions={user.isSuperAdmin ? [{ icon: () => <AddIcon />, tooltip: 'Add badge requirement', isFreeAction: true,
            onClick: event => { returnFocusRef.current = event.currentTarget; setAdding(true); } }, (row) => ({
            icon: () => <DeleteIcon />,
            tooltip: 'Remove badge requirement',
            onClick: event => {
              returnFocusRef.current = event.currentTarget;
              setSelectedRequirement(row);
            },
          })] : []}
        ></DataTable>
      </div>
      {adding && <BadgeRequirementDialog user={user} labId={labId} finalFocusRef={returnFocusRef}
        onClose={() => setAdding(false)} onSaved={() => { setAdding(false); setRevision(value => value + 1); }} />}
      {selectedRequirement && <RemoveBadgeRequirementDialog requirement={selectedRequirement} user={user}
        finalFocusRef={returnFocusRef} onClose={() => setSelectedRequirement(null)} onRemoved={() => {
          returnFocusRef.current = tableRef.current;
          setSelectedRequirement(null);
          setRevision(value => value + 1);
        }} />}
    </Slider>
  );
}

// PropTypes for type-checking component props
EditBadgeMachineTypeTab.propTypes = {
  user: PropTypes.object.isRequired,
  labId: PropTypes.string.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};

export default EditBadgeMachineTypeTab;
